import { useEffect, useState } from "react";
import type { ProductionOverviewData } from "../types/login.types";
import {
  buildRegionDayInfos,
  formatZonedYmdHms,
  type RegionDayInfo,
} from "../../../services/timeZoneService";
import styles from "./StatusCards.module.css";

interface ProductionOverviewCardProps {
  /** 호환용 — 생산 지표는 표시하지 않음 */
  data?: ProductionOverviewData;
  className?: string;
}

const DAY_CLASS: Record<RegionDayInfo["dayKind"], string> = {
  WORKDAY: styles.dayWork,
  WEEKEND: styles.dayRest,
  HOLIDAY: styles.dayHoliday,
};

export function ProductionOverviewCard({ className }: ProductionOverviewCardProps) {
  const [regions, setRegions] = useState<RegionDayInfo[]>([]);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const tick = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(tick);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const infos = await buildRegionDayInfos(new Date());
        if (!cancelled) setRegions(infos);
      } catch {
        if (!cancelled) setRegions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const displayRegions =
    regions.length > 0
      ? regions.map((r) => ({
          ...r,
          dateTime: formatZonedYmdHms(now, r.timeZone),
        }))
      : null;

  return (
    <section
      className={[styles.card, className].filter(Boolean).join(" ")}
      aria-label="한국·중국·일본 날짜와 시간"
    >
      <h2 className={styles.title}>Date · Time · KR · CN · JP</h2>

      <div
        className={styles.regionClockBlock}
        aria-label="한국·중국·일본 날짜와 시간"
      >
        {displayRegions ? (
          displayRegions.map((r) => (
            <div key={r.id} className={styles.regionClockRow}>
              <div className={styles.regionClockLeft}>
                <span className={styles.regionName}>
                  {r.name}
                  <span className={styles.regionAbbr}>{r.abbr}</span>
                </span>
                <time className={styles.regionTime} dateTime={now.toISOString()}>
                  {r.dateTime}
                </time>
              </div>
              <span
                className={[styles.dayBadge, DAY_CLASS[r.dayKind]].join(" ")}
                title={
                  r.holidayName
                    ? `${r.dayLabel}: ${r.holidayName}`
                    : r.dayLabel
                }
              >
                {r.dayLabel}
              </span>
            </div>
          ))
        ) : (
          <p className={styles.widgetHint}>날짜 정보 준비 중…</p>
        )}
      </div>
    </section>
  );
}
